/**
 * dormDecorGeometry — pure geometry helpers shared by the dorm decor planners
 * (planDormDecor rug + fairy-lights, planWindowDecor curtains). No I/O, no
 * Firestore, no React, no THREE.
 *
 * Lifted verbatim from planDormDecor.js so both planners derive wall axes,
 * inward normals, and room geometry the exact same way (no drift). The wall
 * axes come from `extractWallEndpoints` (bathroom-analyzer) → `getWallAxes`
 * (kitchen-assembly), matching how dorm-optimizer-v2 `prepareRoomData` derives
 * wall direction + inward normal + inward-facing rotation.
 */

const { getWallAxes } = require('../kitchen-assembly/wallMath');
const { isPointInsidePolygon } = require('../geometry/pointInPolygon');

const DEFAULT_WALL_THICKNESS_M = 0.15; // 15 cm

/** Centroid of a polygon of {x,z} corners. (Matches dorm-optimizer-v2.) */
function centroid(poly) {
  let sx = 0;
  let sz = 0;
  for (const p of poly) {
    sx += p.x;
    sz += p.z;
  }
  return { x: sx / poly.length, z: sz / poly.length };
}

/** Read the room's floor polygon ({x,z}[]) or null. */
function getFloorPolygon(room) {
  const floor = room?.floors?.[0];
  if (!floor) return null;
  const raw = floor.polygon || floor.polygonCorners;
  if (!Array.isArray(raw) || raw.length < 3) return null;
  return raw.map((p) => ({ x: p.x, z: p.z }));
}

/** Floor Y for the room (floor center height, else 0). */
function getFloorY(room) {
  return room?.floors?.[0]?.center?.y ?? 0;
}

/** Ceiling height for the room = tallest wall's height, else null. */
function getCeilingHeight(walls) {
  let h = 0;
  for (const w of walls) {
    const wh = w?.dimensions?.y || 0;
    if (wh > h) h = wh;
  }
  return h > 0 ? h : null;
}

/**
 * Turn a room wall into `getWallAxes` input (derives bottomLeft/bottomRight from
 * center + rotation via extractWallEndpoints, exactly like the optimizer).
 * Returns { axes, height } or null.
 */
function wallAxesFor(wall, endpointsById) {
  const ep = endpointsById.get(wall.id);
  if (!ep) return null;
  const axes = getWallAxes({
    alignment: { bottomLeft: ep.start, bottomRight: ep.end },
  });
  if (!axes) return null;
  return { axes, height: ep.height ?? wall?.dimensions?.y ?? 0 };
}

/**
 * Inward offset direction (unit vector toward the room interior) at a wall
 * midpoint. `getWallAxes`' normal can point OUTWARD depending on wall vertex
 * winding — using it directly places decor behind the wall (outside the room,
 * invisible). Derive inward from the wall midpoint toward the floor centroid
 * (winding-independent). Falls back to the negated wall normal when the room
 * has no floor polygon.
 *
 * @param {{x:number,z:number}} mid    wall-base midpoint
 * @param {Object} axes                getWallAxes result (has wallNormal)
 * @param {Array<{x:number,z:number}>|null} floorPoly  {x,z}[] or null
 * @returns {{x:number,z:number}} unit inward direction
 */
/** 2D (x-z plane) distance from point `p` to the segment [`a`, `b`]. */
function pointToSegmentDistanceXZ(p, a, b) {
  const abx = b.x - a.x;
  const abz = b.z - a.z;
  const apx = p.x - a.x;
  const apz = p.z - a.z;
  const len2 = abx * abx + abz * abz;
  let t = len2 > 0 ? (apx * abx + apz * abz) / len2 : 0;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  const cx = a.x + t * abx;
  const cz = a.z + t * abz;
  return Math.hypot(p.x - cx, p.z - cz);
}

/**
 * The room wall whose x-z segment is closest to `point` ({x,z}), or null.
 *
 * Used to attach openings/decor to a room wall by geometry when an id lookup
 * fails — dorm rooms carry wall COPIES (`rw-*`) while openings reference the
 * STRUCTURE wall (`sw-*`), with no back-reference between them.
 */
function findNearestWall(point, walls, endpointsById) {
  if (!point || !Array.isArray(walls) || !endpointsById) return null;
  let best = null;
  let bestDist = Infinity;
  for (const w of walls) {
    const ep = endpointsById.get(w.id);
    if (!ep?.start || !ep?.end) continue;
    const d = pointToSegmentDistanceXZ(point, ep.start, ep.end);
    if (d < bestDist) {
      bestDist = d;
      best = w;
    }
  }
  return best;
}

/**
 * The wall's inward PERPENDICULAR, plus the Y-rotation that faces along it.
 *
 * Two separate things have to be right here, and they come from two different
 * places:
 *
 *  - **Perpendicularity** comes from `axes.wallNormal`, which is exactly normal
 *    to the wall by construction. It must NOT come from `inwardNormalAt`, which
 *    points at the floor CENTROID — that is only perpendicular when the centroid
 *    happens to sit on the wall's perpendicular bisector. True of a clean
 *    rectangle, false of an irregular room, and false of an off-centre window.
 *    Using it for rotation tilted decor off its wall by up to 20.9° and splayed
 *    a pair of curtains apart at ±15.9°.
 *  - **Which way is in** comes from the floor polygon: step a hair off the wall
 *    along the normal and ask whether that lands inside the room.
 *    `axes.wallNormal` is the LEFT-hand perpendicular of start → end, so it
 *    inherits the arbitrary endpoint order a scan happens to store — 268 of 290
 *    dev dorm rooms have walls disagreeing with each other about it.
 *
 * Returning them together is the point: every dorm planner used to position
 * decor with the winding-independent vector and rotate it with the
 * winding-dependent one, which is what made the mirror reflect nothing, culled
 * the photo string's polaroids, and hid each polaroid's picture behind its own
 * border.
 *
 * @param {{x:number,z:number}} mid  sample point — pass the WALL's midpoint, not
 *   the decor piece's; see the perpendicularity note above
 * @returns {{x:number, z:number, rotationY:number}} unit inward perpendicular
 *   plus the Y-rotation aligning an object's local +Z with it
 */
function inwardFacingAt(mid, axes, floorPoly) {
  const n = axes.wallNormal;
  // Sign only — `inwardNormalAt` is a reliable indicator of WHICH SIDE is the
  // room even though it is a poor perpendicular. Deliberately not a
  // point-in-polygon probe: a wall's base segment runs along its CENTRELINE,
  // roughly half a wall thickness outside the floor polygon, so a small step
  // along the normal never lands inside and the test inverts. (Measured: it
  // flipped 479 of 28,348 pieces.)
  //
  // Known limit, not currently reachable: this picks the wrong side if a room's
  // centroid falls outside its own floor, as an L-shape's can. Zero of the 295
  // dev dorm rooms are like that, so the more elaborate test stays unwritten
  // rather than unverified.
  const approx = inwardNormalAt(mid, axes, floorPoly);
  const sign = n.x * approx.x + n.z * approx.z >= 0 ? 1 : -1;
  const x = n.x * sign;
  const z = n.z * sign;
  return { x, z, rotationY: Math.atan2(x, z) };
}

function inwardNormalAt(mid, axes, floorPoly) {
  let inX = -axes.wallNormal.x;
  let inZ = -axes.wallNormal.z;
  if (floorPoly) {
    const fc = centroid(floorPoly);
    const tx = fc.x - mid.x;
    const tz = fc.z - mid.z;
    const tl = Math.hypot(tx, tz) || 1;
    inX = tx / tl;
    inZ = tz / tl;
  }
  return { x: inX, z: inZ };
}

/** Is a point inside a floor polygon, in plan? Standard even-odd ray cast. */
function pointInFloorPolygon(p, poly) {
  // The one ray-cast primitive (shared-lib/geometry/pointInPolygon.js), with
  // this module's own guard in front of it. A line-for-line copy of the loop
  // lived here for a day; the codebase already carried four others.
  if (!p || !Array.isArray(poly) || poly.length < 3) return false;
  return isPointInsidePolygon(p, poly);
}

/**
 * Which side of a wall the room is on AT A GIVEN POINT, by probing the floor
 * polygon rather than by asking where the centroid is.
 *
 * `inwardFacingAt` takes the sign from the direction of the centroid, which is
 * a statement about the room as a WHOLE. That is right for a convex room and
 * wrong for a wall whose local interior is not the side the bulk of the room
 * is on — a U-shaped bathroom's inner arm, say, where the centroid sits across
 * the notch. Its docstring anticipates a version of this ("picks the wrong
 * side if a room's centroid falls outside its own floor") but the reachable
 * case is narrower and commoner than that: the centroid is INSIDE the floor in
 * all 73 corpus bathrooms, and a wall still gets the wrong side. Measured on
 * `704C9E88`, a 16-corner bathroom, where a towel bar was hung on the outside
 * face of its wall — outside the room, facing away, invisible from indoors.
 *
 * Returns +1 or -1 against `axes.wallNormal`, or 0 when no probe is decisive,
 * which is the caller's cue to fall back to the centroid rule.
 *
 * The step LADDER is the whole subtlety, in both directions:
 *   - It must clear half the wall's thickness, because a wall's base segment
 *     runs along its CENTRELINE — roughly half a thickness outside the floor
 *     polygon — so a step shorter than that never lands inside and the test
 *     inverts. That is the failure `inwardFacingAt` records as having flipped
 *     479 of 28,348 dorm pieces, and it is why a naive probe was rejected.
 *   - It must stay SMALL, because the question is local. On the corpus wall
 *     above, a 2 in step past the face answers it and a 4 in step reports the
 *     room on both sides — the far arm of the same polygon.
 * So: the smallest step that clears the face and separates the two sides wins,
 * and an ambiguous one is no answer rather than a guess.
 */
function inwardFacingAtStrict(point, axes, floorPoly, wallThickness) {
  const n = axes?.wallNormal;
  if (!n || !point || !Array.isArray(floorPoly) || floorPoly.length < 3)
    return 0;
  const half =
    (Number.isFinite(wallThickness)
      ? wallThickness
      : DEFAULT_WALL_THICKNESS_M) / 2;
  for (const clearance of [0.05, 0.1, 0.2]) {
    const d = half + clearance;
    const plus = pointInFloorPolygon(
      { x: point.x + n.x * d, z: point.z + n.z * d },
      floorPoly
    );
    const minus = pointInFloorPolygon(
      { x: point.x - n.x * d, z: point.z - n.z * d },
      floorPoly
    );
    if (plus !== minus) return plus ? 1 : -1;
  }
  return 0;
}

module.exports = {
  DEFAULT_WALL_THICKNESS_M,
  centroid,
  pointInFloorPolygon,
  inwardFacingAtStrict,
  getFloorPolygon,
  getFloorY,
  getCeilingHeight,
  wallAxesFor,
  inwardNormalAt,
  inwardFacingAt,
  pointToSegmentDistanceXZ,
  findNearestWall,
};
